import { createStore, createEvent, createEffect, combine } from 'effector';
import IPush, { ICreatePush } from '@interfaces/IPush';
import request from '@helpers/request';
import api from '@constants/endpoints';
import { PaginationProps } from 'antd/lib/pagination';
import createPaginationStore from './pagination';
import createPreloadersStore from './preloaders';
import createFiltersStore from './filters';
import removeUndefinedFilters from '@helpers/removeUndefinedFilters';
import { get } from 'lodash';

export const reset = createEvent('Push store reset');

const defaultFilters = {
  sort: 'id,desc',
};

type SurveyItem = {
  key: string;
  text: string;
};

export const getSurveyTopic = createEffect({
  name: 'getSurveyTopic',
  async handler() {
    const response = await request(api.push.getSurveyTopic);
    return response;
  },
});

export const getSurveyText = createEffect({
  name: 'getSurveyText',
  async handler() {
    const response = await request(api.push.getSurveyText);
    return response;
  },
});

export const getSurveyButton = createEffect({
  name: 'getSurveyButton',
  async handler() {
    const response = await request(api.push.getSurveyButton);
    return response;
  },
});

export const getList = createEffect({
  name: 'getList',
  async handler(params?: { pagination?: PaginationProps; sorter?: any }) {
    const paginationParams: PaginationProps = {
      ...pagination.getState(),
      ...params?.pagination,
    };

    const filtersParams = filters.getState();

    const sorterParams: { sort?: string } = { sort: filtersParams.sort };

    if (params?.sorter) {
      sorterParams.sort = params.sorter.order
        ? `${params.sorter.columnKey},${params.sorter.order.replace('end', '')}`
        : defaultFilters.sort;
    }

    const { current } = paginationParams;

    const requestParams = {
      paged: true,
      size: localStorage.getItem('pushPageSize'),
      page: (current || 1) - 1,
      ...defaultFilters,
      ...removeUndefinedFilters({
        ...filtersParams,
      }),
      ...sorterParams,
    };

    const response = await request(api.push.getList, {
      params: requestParams,
    });
    return response;
  },
});

export const createPush = createEffect({
  name: 'createPush',
  async handler(data: Partial<FormData>) {
    const response = await request(api.push.create, {
      data,
    });
    return response;
  },
});

const updateListHandler = () => {
  getList();
};

const { pagination, setPagination } = createPaginationStore({
  getList,
  listName: 'push',
  resetEvent: reset,
  bodyPath: ['data', 'body'],
});

export { setPagination };

const preloaders = createPreloadersStore([getList, createPush]);

const preloadersGetSurvey = createPreloadersStore([getSurveyTopic, getSurveyText, getSurveyButton]);

const list = createStore<IPush[]>([])
  .on(getList.done, (state, { result }) => get(result, ['data', 'body', 'content']))
  .on(createPush.done, updateListHandler)
  .reset(reset);

const listSurveyTopic = createStore<SurveyItem[]>([])
  .on(getSurveyTopic.done, (state, { result }) => {
    const keyValues = get(result, ['data', 'body', 'keyValues']) as Record<string, string>;
    return Object.entries(keyValues).map(([key, text]) => ({ key, text }));
  })
  .reset(reset);

const listSurveyText = createStore<SurveyItem[]>([])
  .on(getSurveyText.done, (state, { result }) => {
    const keyValues = get(result, ['data', 'body', 'keyValues']) as Record<string, string>;
    return Object.entries(keyValues).map(([key, text]) => ({ key, text }));
  })
  .reset(reset);

const listSurveyButton = createStore<SurveyItem[]>([])
  .on(getSurveyButton.done, (state, { result }) => {
    const keyValues = get(result, ['data', 'body', 'keyValues']) as Record<string, string>;
    return Object.entries(keyValues).map(([key, text]) => ({ key, text }));
  })
  .reset(reset);
//   {
//     key: 'push.survey_1.text',
//     text: 'Take a survey: Share your Gameram experience!',
//   },
//   {
//     key: 'push.survey_2.text',
//     text: 'Take a survey: Pick your favorite Gameram features!',
//   },
//   {
//     key: 'push.survey_3.text',
//     text: 'Join the event and win great prizes!',
//   },
// ]);

// const listNotificationKey = createStore<{ key: string; text: string }[]>([
//   {
//     key: 'push.survey.notification_button.take_survey',
//     text: 'Take the survey',
//   },
//   {
//     key: 'push.survey.notification_button.post',
//     text: 'Read more',
//   },
//   {
//     key: 'push.survey.notification_button.reserve',
//     text: 'Go to news',
//   },
// ]);

const { filters, setFilters } = createFiltersStore({
  listName: 'push',
  deaultValues: defaultFilters,
  resetEvent: reset,
});

export { setFilters };

const push = combine({
  preloaders,
  list,
  pagination,
  filters,
  preloadersGetSurvey,
  listSurveyTopic,
  listSurveyText,
  listSurveyButton,
  getSurveyTopic,
  getSurveyText,
  getSurveyButton,
});

export default push;
